import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiPlus, FiTrash2, FiSave,FiPackage } from 'react-icons/fi';
import { productService, stockService } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatCurrency } from '../utils/formatters';

const StockReceive = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [items, setItems] = useState([]);
  const [supplierName, setSupplierName] = useState('');
  const [supplierContact, setSupplierContact] = useState('');
  const [supplierTin, setSupplierTin] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');

  const { data: productsData, isLoading } = useQuery(
    'products',
    productService.getAll
  );

  const receiveMutation = useMutation(stockService.receiveStock, {
    onSuccess: () => {
      toast.success('Stock received successfully');
      queryClient.invalidateQueries('stock-levels');
      queryClient.invalidateQueries('products');
      navigate('/stock');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to receive stock');
    },
  });

  const products = productsData?.data || [];

  const addItem = () => {
    if (!selectedProduct || !quantity || parseInt(quantity) <= 0) {
      toast.error('Please select a product and enter quantity');
      return;
    }

    const product = products.find((p) => p.product_id === selectedProduct);
    if (!product) return;

    const existing = items.findIndex(
      (item) => item.product_id === selectedProduct
    );

    if (existing >= 0) {
      const updated = [...items];
      updated[existing].quantity += parseInt(quantity);
      updated[existing].total_cost =
        updated[existing].quantity * updated[existing].unit_cost;
      setItems(updated);
    } else {
      items.push({
        product_id: product.product_id,
        product_name: product.product_name,
        quantity: parseInt(quantity),
        unit_cost: parseFloat(unitCost) || product.cost_price || 0,
        total_cost:
          parseInt(quantity) * (parseFloat(unitCost) || product.cost_price || 0),
      });
      setItems([...items]);
    }

    setSelectedProduct('');
    setQuantity('');
    setUnitCost('');
  };

  const removeItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const handleSubmit = () => {
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    receiveMutation.mutate({
      items: items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_cost: item.unit_cost,
      })),
      supplier_name: supplierName || null,
      supplier_contact: supplierContact || null,
      supplier_tin: supplierTin || null,
      invoice_number: invoiceNumber || null,
      notes: notes || null,
    });
  };

  const totalItems = items.length;
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalValue = items.reduce((sum, item) => sum + item.total_cost, 0);

  if (isLoading) return <LoadingSpinner message="Loading..." />;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="icon-btn" onClick={() => navigate('/stock')}>
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">Receive Stock</h1>
            <p className="page-subtitle">
              Add received inventory to your stock
            </p>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '24px',
        }}
      >
        <div>
          {/* Add item form */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title" style={{ marginBottom: '16px' }}>
              Add Item
            </h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Product</label>
                <select
                  className="form-select"
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                >
                  <option value="">Select product...</option>
                  {products.map((p) => (
                    <option key={p.product_id} value={p.product_id}>
                      {p.product_name} (Stock: {p.current_stock})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  className="form-input"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  min="1"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Unit Cost (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addItem}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  <FiPlus size={18} />
                  Add Item
                </button>
              </div>
            </div>
          </div>

          {/* Items list */}
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '16px' }}>
              Items to Receive ({totalItems})
            </h2>
            {items.length === 0 ? (
              <div className="empty-state">
                <FiPackage size={48} />
                <h3>No items added yet</h3>
                <p>Add products above to start receiving stock</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Unit Cost</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{item.product_name}</strong>
                        </td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unit_cost)}</td>
                        <td>
                          <strong>{formatCurrency(item.total_cost)}</strong>
                        </td>
                        <td>
                          <button
                            className="icon-btn"
                            onClick={() => removeItem(index)}
                            style={{ color: '#ef4444' }}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {items.length > 0 && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <strong>Total: {totalQuantity} units</strong>
                </div>
                <div>
                  <strong>{formatCurrency(totalValue)}</strong>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Supplier info */}
        <div>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '16px' }}>
              Supplier Information
            </h2>
            <div className="form-group">
              <label className="form-label">Supplier Name</label>
              <input
                type="text"
                className="form-input"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier Contact</label>
              <input
                type="text"
                className="form-input"
                value={supplierContact}
                onChange={(e) => setSupplierContact(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier TIN</label>
              <input
                type="text"
                className="form-input"
                value={supplierTin}
                onChange={(e) => setSupplierTin(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Invoice Number</label>
              <input
                type="text"
                className="form-input"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional"
                rows="3"
              />
            </div>

            <button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={items.length === 0 || receiveMutation.isLoading}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <FiSave size={18} />
              {receiveMutation.isLoading
                ? 'Processing...'
                : 'Confirm Receipt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockReceive;
